import { FC, Fragment, useState } from "react";
import {
    ChangeNames,
    typePostal,
    PostalAddress,
    typeCadaster,
    CadasterAddress,
} from "../../clients/FirebaseClient/types";
import { useDispatch } from "react-redux";
import { FormControlLabel, makeStyles, createStyles, Grid, Box } from "@material-ui/core";
import { AddressCompletionField } from "../core/AddressCompletionField";
import { Button } from "../core/Button";
import { Checkbox } from "../core/Checkbox";
import { useFormModel } from "../core/FormModelHelpes";
import { InfoBox } from "../core/InfoBox";
import { LabledTextField } from "../core/LabledTextField";
import { FamilyPicture } from "../../components/core/FamilyPicture";
import { useTypedSelector, ConfigurationActions, RootDispatch, AuthActions } from "../../redux";
import { Colors } from "../../styles";
import { useStyles } from "../../useStyles";
import GoIconSvg from "../../assets/images/go_icon.svg";
import { useCreateOrLoginCallback } from "../../screens/AvailableProducts";
import { useNavigate } from "react-router-dom";

const useFormControlStyle = makeStyles(() =>
    createStyles({
        container: {
            display: "flex",
            flexDirection: "row",
        },
        containerImage: {},
        containerForm: {
            flex: 1,
        },
        containerFormContent: {
            display: "flex",
            flexDirection: "column",
            marginLeft: 24,
            marginRight: 24,
        },
        formControlLabelStyle: {
            padding: 0,
            paddingLeft: 18,
            fontFamily: "innogyWEB",
            fontStretch: "normal",
            fontStyle: "normal",
            letterSpacing: "normal",
            color: Colors.anthracite,
            fontSize: 22,
        },
        checkboxStyle: {
            marginRight: 8,
        },
        buttonContainer: {
            marginTop: 64,
            marginLeft: 32,
        },
        disclaimerContainer: {
            marginTop: 128,
            fontFamily: "innogyWEB",
            fontStretch: "normal",
            fontStyle: "normal",
            letterSpacing: "normal",
            color: Colors.anthracite50,
            fontSize: 18,
        },
        backbuttonContainer: {
            marginTop: 24,
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-start",
            cursor: "pointer",
        },
        backbuttonImageContainer: {
            display: "flex",
            verticalAlign: "middle",
            alignSelf: "center",
        },
        backbuttonContent: {
            marginLeft: 16,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            verticalAlign: "middle",
            alignSelf: "center",
            fontFamily: "innogyWEB",
            fontSize: 22,
            fontStretch: "normal",
            fontStyle: "normal",
            letterSpacing: "normal",
            lineHeight: 1.4,
        },
        bottomHr: {
            marginTop: 24,
        },
    }),
);

const postalToCadaster = (postalAddress: PostalAddress): CadasterAddress => ({
    type: typeCadaster,
    city: postalAddress.city,
    street: postalAddress.street,
    houseNumber: postalAddress.houseNumber,
    zipCode: postalAddress.zipCode,
    district: postalAddress.district,
    mark: {
        value: "",
        error: null,
    },
    parcel: {
        value: "",
        error: null,
    },
});

const cadasterToPostal = (cadasterAddress: CadasterAddress): PostalAddress => ({
    type: typePostal,
    city: cadasterAddress.city,
    street: {
        value: cadasterAddress.street?.value || "",
        error: null,
    },
    houseNumber: {
        value: cadasterAddress.houseNumber?.value || "",
        error: null,
    },
    zipCode: cadasterAddress.zipCode || {
        value: "",
        error: null,
    },
    district: cadasterAddress.district,
});

export const BuildingAddressForm: FC = () => {
    const classes = useStyles();
    const formControlClasses = useFormControlStyle();
    const navigate = useNavigate();
    const [corrected, setCorrected] = useState(false);
    const [addressNotFound, setAddressNotFound] = useState(false);
    const isLoggedIn = useTypedSelector((state) => state.auth.uid);
    const dispatch: RootDispatch = useDispatch();
    const uid = useTypedSelector((state) => state.auth.uid);

    const { values, setValues } = useFormModel<ChangeNames.BUILDING_ADDRESS>();

    const buildingAddress = values.buildingAddress;
    const isPostalAddress = buildingAddress.type === typePostal;

    const isAddressInvalid = (): boolean => {
        const ba = values.buildingAddress;
        if (ba.type === typePostal) {
            return (
                !ba.zipCode.value.trim() ||
                !ba.city.value.trim() ||
                !ba.houseNumber.value.trim() ||
                !ba.street?.value.trim()
            );
        } else {
            return (
                !ba.zipCode.value.trim() ||
                !ba.city.value.trim() ||
                !ba.mark.value.trim() ||
                !ba.parcel.value.trim()
            );
        }
    };

    const createOrLogin = useCreateOrLoginCallback(uid, buildingAddress);

    return (
        <Grid
            container
            spacing={0}
            className={formControlClasses.container}
            data-testid="buildingAddress-testId"
        >
            <Box sx={{ display: { xs: "none", md: "block" } }}>
                <div className={formControlClasses.containerImage}>
                    <FamilyPicture />
                </div>
            </Box>
            <div className={formControlClasses.containerForm}>
                <div className={formControlClasses.containerFormContent}>
                    <h3 className={classes.h3} style={{ marginTop: 40, marginBottom: 0 }}>
                        Meine Bauadresse
                    </h3>
                    <p className={classes.p}>
                        Hier erfassen Sie die Bauadresse, an der die Maßnahme durchgeführt werden
                        soll.
                    </p>
                    {corrected && (
                        <InfoBox title="Korrektur" style={{ marginTop: 0, marginBottom: 25 }}>
                            <p>
                                Wir haben ihre Eingabe zur ähnlichsten bekannten Adresse geändert,
                                bitte prüfen Sie die Korrektur.
                            </p>
                        </InfoBox>
                    )}
                    {addressNotFound && (
                        <InfoBox
                            title="Unbekannte Adresse"
                            style={{ marginTop: 0, marginBottom: 25 }}
                        >
                            <p>
                                Die Adresse konnte nicht verifiziert werden, bitte prüfen Sie ihre
                                Eingabe.
                            </p>
                        </InfoBox>
                    )}
                    <Grid container spacing={2}>
                        <Grid item md={3} sm={12} xs={12}>
                            <AddressCompletionField
                                label="PLZ *"
                                zipCode={values.buildingAddress.zipCode.value}
                                city={values.buildingAddress.city.value}
                                street={values.buildingAddress.street?.value}
                                complete="zipCode"
                                validate={true}
                                maxLength={5}
                                onChange={(newValue) =>
                                    setValues((v) => {
                                        setAddressNotFound(false);
                                        setCorrected(false);
                                        v.buildingAddress.zipCode.value = newValue;
                                    })
                                }
                            />
                        </Grid>

                        <Grid item md={9} sm={12} xs={12}>
                            <AddressCompletionField
                                label="Ort *"
                                zipCode={values.buildingAddress.zipCode.value}
                                city={values.buildingAddress.city.value}
                                street={values.buildingAddress.street?.value}
                                complete="city"
                                validate={true}
                                maxLength={40}
                                onChange={(newValue) =>
                                    setValues((v) => {
                                        setAddressNotFound(false);
                                        setCorrected(false);
                                        v.buildingAddress.city.value = newValue;
                                    })
                                }
                            />
                        </Grid>

                        <Grid item md={9} sm={12} xs={12}>
                            <LabledTextField
                                data-testid={"buildingAddress.district"}
                                name={"buildingAddress.district"}
                                label={"Ortsteil"}
                                style={{ width: "100%", flex: 1 }}
                                margin="normal"
                                variant="outlined"
                                error={!!buildingAddress.district?.error}
                                helperText={buildingAddress.district?.error}
                                value={buildingAddress.district?.value || ""}
                                inputProps={{
                                    maxLength: 40,
                                }}
                                onChange={(event) =>
                                    setValues((values) => {
                                        const address = values.buildingAddress;
                                        address.district = {
                                            value: event.target.value,
                                            error: buildingAddress.district?.error,
                                        };
                                    })
                                }
                            />
                        </Grid>

                        <Grid item md={9} sm={12} xs={12}>
                            <AddressCompletionField
                                label={`Straße${isPostalAddress ? " *" : ""}`}
                                zipCode={values.buildingAddress.zipCode.value}
                                city={values.buildingAddress.city.value}
                                street={values.buildingAddress.street?.value}
                                complete="street"
                                validate={true}
                                maxLength={60}
                                onChange={(newValue) =>
                                    setValues((v) => {
                                        setAddressNotFound(false);
                                        setCorrected(false);
                                        v.buildingAddress.street = { value: newValue };
                                    })
                                }
                            />
                        </Grid>

                        <Grid item md={3} sm={12} xs={12}>
                            <LabledTextField
                                data-testid={"buildingAddress.hausNumber"}
                                name={"buildingAddress.hausNumber"}
                                label={`Hausnummer${isPostalAddress ? " *" : ""}`}
                                style={{ width: "100%", flex: 1 }}
                                margin="normal"
                                variant="outlined"
                                error={!!buildingAddress.houseNumber?.error}
                                helperText={buildingAddress.houseNumber?.error}
                                value={buildingAddress.houseNumber?.value || ""}
                                autoComplete="nope"
                                inputProps={{
                                    maxLength: 10,
                                }}
                                onChange={(event) =>
                                    setValues((values) => {
                                        values.buildingAddress.houseNumber = {
                                            value: event.target.value,
                                            error: buildingAddress.houseNumber?.error,
                                        };
                                    })
                                }
                            />
                        </Grid>
                    </Grid>
                    <div style={{ marginTop: 40 }}>
                        <FormControlLabel
                            classes={{ label: formControlClasses.formControlLabelStyle }}
                            control={
                                <Checkbox
                                    className={formControlClasses.checkboxStyle}
                                    value="unknownStreetAndHouseNumber"
                                    checked={!isPostalAddress}
                                    onChange={() =>
                                        setValues((values) => {
                                            const address = buildingAddress;
                                            if (address.type === typePostal) {
                                                values.buildingAddress = postalToCadaster(address);
                                            } else if (address.type === typeCadaster) {
                                                values.buildingAddress = cadasterToPostal(address);
                                            } else {
                                                throw Error("unknown type for address");
                                            }
                                        })
                                    }
                                />
                            }
                            label="Die Straße steht nicht zur Auswahl bzw. die Hausnummer ist nicht bekannt."
                        />
                    </div>

                    <div>
                        {buildingAddress.type === typeCadaster && (
                            <Fragment>
                                <div style={{ marginTop: 40 }}>
                                    <Grid container spacing={2}>
                                        <Grid item md={6} sm={12} xs={12}>
                                            <LabledTextField
                                                data-testid={"buildingAddress.mark"}
                                                name={"buildingAddress.mark"}
                                                label={"Gemarkung *"}
                                                style={{ width: "100%", flex: 1 }}
                                                margin="normal"
                                                variant="outlined"
                                                error={!!buildingAddress.mark.error}
                                                helperText={buildingAddress.mark?.error}
                                                value={buildingAddress.mark?.value || ""}
                                                inputProps={{
                                                    maxLength: 50,
                                                }}
                                                onChange={(event) =>
                                                    setValues((values) => {
                                                        const address = values.buildingAddress;
                                                        if (address.type === typeCadaster) {
                                                            address.mark = {
                                                                value: event.target.value,
                                                                error: buildingAddress.mark?.error,
                                                            };
                                                        }
                                                    })
                                                }
                                            />
                                        </Grid>

                                        <Grid item md={3} sm={12} xs={12}>
                                            <LabledTextField
                                                data-testid={"buildingAddress.corridor"}
                                                name={"buildingAddress.corridor"}
                                                label={"Flur"}
                                                style={{ width: "100%", flex: 1 }}
                                                margin="normal"
                                                variant="outlined"
                                                error={!!buildingAddress.corridor?.error}
                                                helperText={buildingAddress.corridor?.error}
                                                value={buildingAddress.corridor?.value || ""}
                                                inputProps={{
                                                    maxLength: 15,
                                                }}
                                                onChange={(event) =>
                                                    setValues((values) => {
                                                        const address = values.buildingAddress;
                                                        if (address.type === typeCadaster) {
                                                            address.corridor = event.target.value
                                                                ? {
                                                                      value: event.target.value,
                                                                      error: buildingAddress
                                                                          .corridor?.error,
                                                                  }
                                                                : undefined;
                                                        }
                                                    })
                                                }
                                            />
                                        </Grid>

                                        <Grid item md={3} sm={12} xs={12}>
                                            <LabledTextField
                                                data-testid={"buildingAddress.parcel"}
                                                name={"buildingAddress.parcel"}
                                                label={"Flurstück *"}
                                                style={{ width: "100%", flex: 1 }}
                                                margin="normal"
                                                variant="outlined"
                                                error={!!buildingAddress.parcel?.error}
                                                helperText={buildingAddress.parcel?.error}
                                                value={buildingAddress.parcel?.value}
                                                inputProps={{
                                                    maxLength: 15,
                                                }}
                                                onChange={(event) =>
                                                    setValues((values) => {
                                                        const address = values.buildingAddress;
                                                        if (address.type === typeCadaster) {
                                                            address.parcel = {
                                                                value: event.target.value,
                                                                error: buildingAddress.parcel
                                                                    ?.error,
                                                            };
                                                        }
                                                    })
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                </div>
                            </Fragment>
                        )}
                    </div>

                    <div
                        style={{
                            flexDirection: "row-reverse",
                            display: "flex",
                            marginTop: 24,
                        }}
                    >
                        <div className={formControlClasses.buttonContainer}>
                            <Button
                                mode="primary"
                                style={{ marginLeft: 32 }}
                                disabled={isAddressInvalid()}
                                onClick={async () => {
                                    const response = await dispatch(
                                        ConfigurationActions.requestPartner(values),
                                    );
                                    if (!response) {
                                        setAddressNotFound(true);
                                        return;
                                    }

                                    if (response.verificationSkipped) {
                                        createOrLogin();
                                        return;
                                    }
                                    if (response?.corrected) {
                                        setValues((values) => {
                                            values.buildingAddress.zipCode.value =
                                                response.address.zipCode;
                                            values.buildingAddress.city.value =
                                                response.address.city;
                                            values.buildingAddress.street = {
                                                value: response.address.street,
                                            };
                                            values.buildingAddress.houseNumber = {
                                                value: response.address.houseNumber,
                                            };
                                        });
                                        setCorrected(true);
                                        return;
                                    }
                                    navigate("/products");
                                }}
                            >
                                Zuständigkeit prüfen
                            </Button>
                        </div>
                    </div>
                    <div className={formControlClasses.bottomHr}>
                        <hr className={classes.hr} />
                    </div>
                    {isLoggedIn ? (
                        <Fragment />
                    ) : (
                        <div className={formControlClasses.backbuttonContainer}>
                            <div className={formControlClasses.backbuttonImageContainer}>
                                <img alt="" src={GoIconSvg} />
                            </div>
                            <div
                                onClick={() => dispatch(AuthActions.signInWithMSP())}
                                className={formControlClasses.backbuttonContent}
                            >
                                Anmelden
                            </div>
                        </div>
                    )}

                    <div className={formControlClasses.disclaimerContainer}>
                        <p>* Mit Sternchen gekennzeichnete Felder sind Pflichtfelder.</p>
                    </div>
                </div>
            </div>
        </Grid>
    );
};
